import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Message, Session, UnreadConversation } from 'talkjs/all';
import { CONVERSATION_ID_PREFIX, type ConversationType } from './constants';
import {
  summaryFromTalkJsConversationSnapshot,
  summaryFromTalkJsMessage,
  summaryFromTalkJsUnreadConversation,
} from './summaries';
import type {
  ConversationSummariesWithLoadingState,
  ConversationSummaryWithLoadingState,
  ConversationsContextValue,
} from './types';

const ConversationsContext = createContext<ConversationsContextValue>({
  getConversationSummary: () => {
    throw new Error(
      "getConversationSummary called outside of ConversationsProvider's context",
    );
  },
  getUnreadConversationCount: () => {
    throw new Error(
      "getUnreadConversationCount called outside of ConversationsProvider's context",
    );
  },
});

/**
 * TalkJS `unreads.onChange` won't fire for a message that arrives while the
 * user already has the conversation open (TalkJS auto-marks it read), so we
 * refresh lastMessage independently on every inbound message.
 */
function useSubscribeToTalkJsMessageReceivedCallbacks(
  session: Session | null,
  setSummaries: React.Dispatch<
    React.SetStateAction<ConversationSummariesWithLoadingState>
  >,
) {
  useEffect(() => {
    if (!session) return;

    const subscription = session.onMessage((message: Message) => {
      setSummaries(prevSummaries => {
        const existing = prevSummaries.get(message.conversation.id);
        // Preserve the authoritative isUnread flag set by `unreads.onChange`.
        // `onMessage` fires for every inbound message; it's only responsible
        // for refreshing `lastMessage`, not for deciding read/unread. If we
        // reset `isUnread` to false here, we race with `unreads.onChange` and
        // can clobber its correct value.
        const existingIsUnread =
          existing && !existing.isLoading
            ? (existing.summary?.isUnread ?? false)
            : false;
        const nextSummaries = new Map(prevSummaries);
        nextSummaries.set(message.conversation.id, {
          isLoading: false,
          summary: summaryFromTalkJsMessage(message, existingIsUnread),
        });
        return nextSummaries;
      });
    });

    return () => subscription.unsubscribe();
  }, [session, setSummaries]);
}

/**
 * Subscribes to unread conversation change notifications in TalkJS.
 * @param session TalkJS session for the current user.
 * @param setSummaries The state setter to update conversation summaries in response to unread conversation changes.
 */
function useSubscribeToTalkJsUnreadCallbacks(
  session: Session | null,
  setSummaries: React.Dispatch<
    React.SetStateAction<ConversationSummariesWithLoadingState>
  >,
) {
  useEffect(() => {
    if (!session) return;

    // TODO: Migrate `session.unreads` to the TalkJS JavaScript Data API's
    //   `Session.subscribeConversations` once we upgrade our self-hosted TalkJS
    //   SDK (see `utils/devbox3/roles/common/vars/main.yaml` →
    //   `talkjs_image_version`). Today the we use the Data API's
    //   `ConversationRef.get()` but not `Session.subscribeConversations()`.
    //   Consumers see only `ConversationSummary` + `useConversationSummary` —
    //   no TalkJS types leak across this boundary, so the eventual Data API
    //   upgrade will be contained to this file.
    const subscription = session.unreads.onChange(
      (currentUnreadConversations: UnreadConversation[]) => {
        setSummaries(prevSummaries => {
          /* `currentUnreadConversations` is the full authoritative list of
          conversations that are unread right now (not a delta). To reconcile
          it with our cache:

          1. Walk every entry in `prevSummaries` and flip `isUnread` to match
             whether it appears in the new emission. Entries that dropped out
             of the emission are now read; entries that stayed or were added
             are unread. lastMessage is preserved so previews don't disappear.
          2. Overwrite entries from step 1 with the fresh lastMessage data
             from `currentUnreadConversations` — TalkJS's emission carries
             the most recent message for each unread conversation.
          */
          const isCurrentlyUnread = new Set(
            currentUnreadConversations.map(c => c.conversation.id),
          );

          const nextSummaries = new Map<
            string,
            ConversationSummaryWithLoadingState
          >();

          prevSummaries.forEach((state, id) => {
            if (state.isLoading || !state.summary) {
              // Loading or not-found entries carry through unchanged — they
              // have no `isUnread` flag to flip.
              nextSummaries.set(id, state);
              return;
            }
            nextSummaries.set(id, {
              isLoading: false,
              summary: {
                ...state.summary,
                isUnread: isCurrentlyUnread.has(id),
              },
            });
          });

          for (const unread of currentUnreadConversations) {
            nextSummaries.set(unread.conversation.id, {
              isLoading: false,
              summary: summaryFromTalkJsUnreadConversation(unread),
            });
          }

          return nextSummaries;
        });
      },
    );

    return () => subscription.unsubscribe();
  }, [session, setSummaries]);
}

/**
 * Provides information about the current state of conversations. The current
 * backend is using TalkJS SDK. However, the exposed types of this provider and
 * the related hooks such as @see useConversationSummary should not expose any
 * TalkJS-specific type.
 *
 * NOTE: ConversationsProvider/useConversationSummary should work for
 *   TalkJS-based conversation. It is not coupled to interagency chats, albeit
 *   that is the only place it is currently used.
 */
export function ConversationsProvider({
  session,
  children,
}: {
  session: Session | null;
  children: ReactNode;
}) {
  // One new Map per mutation triggers every useContext consumer to re-render.
  // List sizes are small (tens of conversations per user), so per-row
  // memoization isn't worth the complexity.
  const [summaries, setSummaries] =
    useState<ConversationSummariesWithLoadingState>(() => new Map());

  /**
   * Fetches a conversation snapshot from TalkJS. Resolves to a snapshot, or
   * null if the user isn't a participant / the conversation doesn't exist.
   * Rejections propagate to the caller.
   */
  const getConversationSnapshot = useCallback(
    async (conversationId: string) => {
      if (!session) return;
      return session.conversation(conversationId).get();
    },
    [session],
  );

  const fetchAndCacheConversationSummary = useCallback(
    (conversationId: string) => {
      // Write the loading state to the cache before firing `.get()` so that any
      // re-render between now and the fetch resolving sees `{ isLoading: true
      // }` and doesn't re-fire another request.
      setSummaries(prevSummaries => {
        if (prevSummaries.has(conversationId)) return prevSummaries;
        const nextSummaries = new Map(prevSummaries);
        nextSummaries.set(conversationId, {
          isLoading: true,
          summary: null,
        });
        return nextSummaries;
      });

      getConversationSnapshot(conversationId)
        .then(snapshot => {
          setSummaries(prevSummaries => {
            const nextSummaries = new Map(prevSummaries);
            nextSummaries.set(conversationId, {
              isLoading: false,
              summary: snapshot
                ? summaryFromTalkJsConversationSnapshot(snapshot)
                : null,
            });
            return nextSummaries;
          });
        })
        .catch(error => {
          // eslint-disable-next-line no-console -- console.error logs to sentry
          console.error(`Failed to fetch conversation snapshot`, {
            error,
            conversationId,
          });
          // to prevent us continuoually trying to fetch a conversation that's
          // errored, we cache the "not found" state. If a message is received
          // or it becomes unread, we'll get a notification from TalkJS and
          // refresh the cache.
          setSummaries(prevSummaries => {
            const nextSummaries = new Map(prevSummaries);
            nextSummaries.set(conversationId, {
              isLoading: false,
              summary: null,
            });
            return nextSummaries;
          });
        });
    },
    [getConversationSnapshot],
  );

  useSubscribeToTalkJsUnreadCallbacks(session, setSummaries);
  useSubscribeToTalkJsMessageReceivedCallbacks(session, setSummaries);

  const getUnreadConversationCount = useCallback(
    (type: ConversationType): number => {
      // Runtime guard for JS callers / future types that haven't been wired up
      // yet. The union narrows this to a no-op today, but keeps the provider
      // honest if a new type is added to `ConversationType` before it has a
      // prefix or counting semantics defined.
      if (type !== 'interagency') {
        throw new Error(
          `getUnreadConversationCount: unsupported conversation type "${String(type)}"`,
        );
      }
      const prefix = CONVERSATION_ID_PREFIX[type];
      let count = 0;
      // `Array.from` because the project's tsconfig target (es5) doesn't allow
      // `for...of` directly on a Map without downlevelIteration.
      for (const [id, state] of Array.from(summaries)) {
        if (!id.startsWith(prefix)) continue;
        if (!state.isLoading && state.summary?.isUnread) count++;
      }
      return count;
    },
    [summaries],
  );

  const contextImplementation = useMemo(() => {
    return {
      getConversationSummary: (
        conversationId: string,
      ): ReturnType<ConversationsContextValue['getConversationSummary']> => {
        const summary = summaries.get(conversationId);

        if (!summary) {
          // Trigger the fetch but and indicate it's loading:
          fetchAndCacheConversationSummary(conversationId);
          return {
            isLoading: true,
            summary: null,
          };
        }
        return summary;
      },
      getUnreadConversationCount,
    };
  }, [fetchAndCacheConversationSummary, getUnreadConversationCount, summaries]);

  return (
    <ConversationsContext.Provider value={contextImplementation}>
      {children}
    </ConversationsContext.Provider>
  );
}

/**
 * Returns the cached ConversationSummary for a TalkJS conversation, or null
 * while the initial `.get()` is in flight or if the user isn't a participant.
 * Triggers a one-time fetch on mount per conversation ID. Subscription deltas
 * (unread state, lastMessage) keep the cache fresh for the lifetime of the
 * provider.
 */
export function useConversationSummary(
  conversationId: string,
): ReturnType<ConversationsContextValue['getConversationSummary']> {
  const context = useContext(ConversationsContext);
  return context.getConversationSummary(conversationId);
}

/**
 * Returns the number of conversations of the given `type` with at least one
 * unread message for the current user. Reactively updates as TalkJS emits
 * unread-state changes.
 */
export function useUnreadConversationCount(type: ConversationType): number {
  return useContext(ConversationsContext).getUnreadConversationCount(type);
}
